package org.zq.astrock_ai.ui.research.gushi.components

import org.zq.astrock_ai.util.formatDecimal

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.astrock_ai.ui.research.gushi.data.highlightChatText
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.zq.astrock_ai.api.dto.AgentChatRequestDto
import org.zq.astrock_ai.api.dto.QuantResultDto
import org.zq.astrock_ai.api.dto.QuantScanResponseDto
import org.zq.astrock_ai.api.dto.StructuredAnalysisDto
import org.zq.astrock_ai.theme.LocalAppFontFamily
import org.zq.astrock_ai.network.EastMoneyMarketContextClient
import org.zq.astrock_ai.network.EastMoneyNewsClient
import org.zq.astrock_ai.network.LocalStockChatEngine
import org.zq.astrock_ai.network.MarketRepository
import org.zq.astrock_ai.network.MoonshotDirectClient
import org.zq.astrock_ai.network.StockAgentApiClient
import org.zq.astrock_ai.network.StockNewsCategory
import org.zq.astrock_ai.network.moonshotApiKey
import org.zq.astrock_ai.network.moonshotModel
import org.zq.astrock_ai.quant.ZoneMarketContext
import org.zq.astrock_ai.quant.buildBuyZonePlan
import org.zq.astrock_ai.quant.buildSellZonePlan
import org.zq.astrock_ai.quant.toQuantBars
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.data.AdaptiveLearningResult
import org.zq.astrock_ai.ui.research.gushi.data.DemoData
import org.zq.astrock_ai.ui.research.gushi.data.ResearchChatBrief
import org.zq.astrock_ai.ui.research.gushi.data.StockTrendMetrics
import org.zq.astrock_ai.ui.research.gushi.data.ChatHistoryStore
import org.zq.astrock_ai.ui.research.gushi.data.PaperTradeStore
import org.zq.astrock_ai.ui.research.gushi.data.detectQuantStrategyId
import org.zq.astrock_ai.ui.research.gushi.data.localQuantPlan
import org.zq.astrock_ai.ui.research.gushi.data.resolveStrategyParams
import org.zq.astrock_ai.ui.research.gushi.data.strategyDefaultParams
import org.zq.astrock_ai.ui.research.gushi.data.strategyParamPreset
import org.zq.astrock_ai.ui.research.gushi.data.wantsQuantQuestion
import org.zq.astrock_ai.ui.research.gushi.data.wantsScanQuestion
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import org.zq.astrock_ai.ui.research.gushi.model.ChatMessage
import org.zq.astrock_ai.ui.research.gushi.model.HoldingPosition
import org.zq.astrock_ai.ui.research.gushi.model.NewsItem
import org.zq.astrock_ai.ui.research.gushi.model.NewsType
import org.zq.astrock_ai.ui.research.gushi.model.StockItem

@Composable
fun AiAssistantPanel(
    stock: StockItem,
    metrics: StockTrendMetrics? = null,
    holding: HoldingPosition? = null,
    candles: List<Candle> = emptyList(),
    adaptiveLearning: AdaptiveLearningResult = AdaptiveLearningResult(),
    watchlistCodes: List<String> = emptyList(),
    onEditHolding: () -> Unit = {},
    onScanResult: (QuantScanResponseDto) -> Unit = {},
    onQuantUpdated: (QuantResultDto?) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()
    val remoteApi = remember { StockAgentApiClient() }
    val marketRepo = remember { MarketRepository() }
    val localEngine = remember { LocalStockChatEngine() }
    val moonshot = remember { MoonshotDirectClient() }
    val newsClient = remember { EastMoneyNewsClient() }
    val contextClient = remember { EastMoneyMarketContextClient() }
    var sessionId by remember(stock.code) { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    var plan by remember(stock.code) { mutableStateOf<StructuredAnalysisDto?>(null) }
    var quant by remember(stock.code) { mutableStateOf<QuantResultDto?>(null) }
    var selectedStrategyId by remember(stock.code) { mutableStateOf("dual_ma") }
    var lookbackDays by remember(stock.code) { mutableStateOf(90) }
    var paramPresetId by remember(stock.code) { mutableStateOf("default") }
    var customParams by remember(stock.code, selectedStrategyId) {
        mutableStateOf(strategyDefaultParams(selectedStrategyId))
    }
    var quantLoading by remember(stock.code) { mutableStateOf(false) }
    var compareRows by remember(stock.code) { mutableStateOf<List<StrategyCompareRow>>(emptyList()) }
    var compareLoading by remember(stock.code) { mutableStateOf(false) }
    var engineMode by remember(stock.code) { mutableStateOf("检测中…") }
    val hasLocalKey = remember { !moonshotApiKey().isNullOrBlank() }
    val demoFallback = remember(stock.code) { DemoData.trendMetrics(stock) }
    val fallback = metrics ?: demoFallback
    val messages = remember(stock.code) {
        mutableStateListOf<ChatMessage>().also { list ->
            val saved = ChatHistoryStore.load(stock.code)
            if (saved.isNotEmpty()) {
                list.addAll(saved)
            } else {
                list.add(
                    ChatMessage(
                        isUser = false,
                        text = "我是智启股势。已切换至 ${stock.name}（${stock.code}）。\n正在检测真实大模型通道…",
                        time = "系统",
                    ),
                )
            }
        }
    }
    val restoredFromLocal = remember(stock.code) {
        ChatHistoryStore.load(stock.code).isNotEmpty()
    }

    // 消息条数或最后一条内容变化时落盘（回答替换「思考中」时 size 不变也要保存）
    val lastMsgFingerprint = messages.lastOrNull()?.let { "${it.isUser}|${it.text.length}|${it.text.hashCode()}" }
    LaunchedEffect(stock.code, messages.size, lastMsgFingerprint) {
        ChatHistoryStore.save(stock.code, messages.toList())
    }

    // 切股 / 换策略 / 回看 / 参数 / 持仓：默认参数可走云端，自定义参数走本地引擎
    LaunchedEffect(
        stock.code,
        candles,
        selectedStrategyId,
        lookbackDays,
        paramPresetId,
        customParams,
        holding?.costPrice,
        holding?.quantity,
    ) {
        quantLoading = true
        val cost = holding?.costPrice
        val qty = holding?.quantity
        val params = resolveStrategyParams(selectedStrategyId, paramPresetId, customParams)
        val remote = runCatching {
            marketRepo.loadQuantResult(
                stock.code,
                strategyId = selectedStrategyId,
                lookbackDays = lookbackDays,
                costPrice = cost,
                quantity = qty,
                params = params,
            )
        }.getOrNull()
        quant = remote
            ?: if (candles.size >= 20) {
                candles.localQuantPlan(
                    strategyId = selectedStrategyId,
                    lookbackDays = lookbackDays,
                    costPrice = cost,
                    quantity = qty,
                    params = params,
                )
            } else {
                null
            }
        onQuantUpdated(quant)
        quantLoading = false
    }
    var input by remember { mutableStateOf("") }
    var deskTab by remember(stock.code) { mutableStateOf(0) } // 0 建议 / 1 量化
    val panelScroll = rememberScrollState()

    LaunchedEffect(stock.code) {
        val health = runCatching { remoteApi.health() }.getOrNull()
        engineMode = when {
            health?.kimiConfigured == true -> "云端 Kimi 可用"
            hasLocalKey -> "本地 Kimi K3 可用"
            else -> "未配置真实大模型"
        }
        // 已有本地历史时不要覆盖首条；仅欢迎占位语才更新通道说明
        val first = messages.firstOrNull()
        val isPlaceholder = first != null &&
            !first.isUser &&
            (first.text.contains("正在检测") || (first.time == "系统" && messages.size == 1))
        if (!restoredFromLocal && isPlaceholder) {
            messages[0] = ChatMessage(
                isUser = false,
                text = buildString {
                    append("我是智启股势。已切换至 ${stock.name}（${stock.code}）。\n")
                    append("每次提问前，会先把当前行情、技术面、资金、新闻、训练结果和持仓一并告诉模型。\n")
                    when {
                        health?.kimiConfigured == true ->
                            append("当前可走云端 Kimi 真实回答。")
                        hasLocalKey ->
                            append("云端未配置密钥，将使用本地密钥直连 Kimi K3（api.moonshot.cn）真实回答。")
                        else ->
                            append(
                                "当前云端未配置 MOONSHOT_API_KEY，本机也未找到密钥，" +
                                    "暂时只能用本地指标模板回答（非大模型）。\n" +
                                    "请在项目根目录 secrets.local.properties 写入 MOONSHOT_API_KEY=你的密钥后重启，" +
                                    "或给线上服务配置同样密钥。",
                            )
                    }
                },
                time = "系统",
            )
        }
    }

    LaunchedEffect(messages.size, panelScroll.maxValue) {
        if (messages.isNotEmpty() && panelScroll.maxValue > 0) {
            panelScroll.animateScrollTo(panelScroll.maxValue)
        }
    }

    fun ask(question: String) {
        val text = question.trim()
        if (text.isEmpty() || busy) return
        messages += ChatMessage(true, text, "现在")
        busy = true
        messages += ChatMessage(false, "正在汇总当前数据并分析 ${stock.name}…", "现在")
        val thinkingIndex = messages.lastIndex
        val parsedCost = Regex("""成本[^\d]{0,4}(\d+(?:\.\d+)?)""")
            .find(text)?.groupValues?.getOrNull(1)?.toDoubleOrNull()
        val parsedQty = Regex("""(\d+)\s*股""")
            .find(text)?.groupValues?.getOrNull(1)?.toIntOrNull()
        scope.launch {
            val cost = parsedCost ?: holding?.costPrice
            val qty = parsedQty ?: holding?.quantity
            val (market, news) = coroutineScope {
                val marketDeferred = async {
                    runCatching { contextClient.fetch(stock.code) }.getOrNull()
                }
                val newsDeferred = async {
                    runCatching {
                        newsClient.fetchLatest(stock.code).map { entry ->
                            NewsItem(
                                time = entry.publishedAt,
                                type = when (entry.category) {
                                    StockNewsCategory.NEWS -> NewsType.NEWS
                                    StockNewsCategory.CAPITAL -> NewsType.CAPITAL
                                    StockNewsCategory.ANNOUNCEMENT -> NewsType.ANNOUNCEMENT
                                    StockNewsCategory.FINANCIAL -> NewsType.FINANCIAL
                                    StockNewsCategory.RESEARCH -> NewsType.RESEARCH
                                },
                                title = entry.title,
                                source = entry.source,
                                impact = "实时",
                                url = entry.url,
                            )
                        }
                    }.getOrDefault(emptyList())
                }
                marketDeferred.await() to newsDeferred.await()
            }
            val (buyZonePlan, sellZonePlan) = runCatching {
                val bars = marketRepo.loadKlineBars(stock.code, limit = 120).toQuantBars()
                val px = stock.price.replace(",", "").toDoubleOrNull() ?: 0.0
                val zoneMarket = ZoneMarketContext(
                    todayMainNet = market?.todayMainNet,
                    recentMainNet = market?.recentMainNet,
                    headlines = news.map { it.title }.filter { it.isNotBlank() }.take(16),
                )
                buildBuyZonePlan(bars, px, market = zoneMarket) to
                    buildSellZonePlan(bars, px, market = zoneMarket)
            }.getOrElse { null to null }
            val brief = ResearchChatBrief.build(
                stock = stock,
                metrics = fallback,
                holding = holding,
                candles = candles,
                market = market,
                news = news,
                adaptive = adaptiveLearning,
                costPrice = cost,
                quantity = qty,
                buyLow = buyZonePlan?.low ?: 0.0,
                buyHigh = buyZonePlan?.high ?: 0.0,
                buyMethod = buyZonePlan?.method.orEmpty(),
                buyConfidence = buyZonePlan?.confidence ?: 0.0,
                buyReasons = buyZonePlan?.reasons.orEmpty(),
                sellLow = sellZonePlan?.low ?: 0.0,
                sellHigh = sellZonePlan?.high ?: 0.0,
                sellMethod = sellZonePlan?.method.orEmpty(),
                sellConfidence = sellZonePlan?.confidence ?: 0.0,
                sellReasons = sellZonePlan?.reasons.orEmpty(),
            )
            val request = AgentChatRequestDto(
                stockCode = stock.code,
                stockName = stock.name,
                message = text,
                sessionId = sessionId,
                costPrice = cost,
                quantity = qty,
                researchBrief = brief,
                watchlistCodes = watchlistCodes,
            )

            val remote = runCatching { remoteApi.chat(request) }.getOrNull()
            val remoteIsReal = remote != null &&
                remote.error == null &&
                remote.replyText.isNotBlank() &&
                remote.mode == "kimi"

            val response = when {
                remoteIsReal -> {
                    engineMode = "云端 Kimi"
                    remote
                }
                hasLocalKey -> {
                    val key = moonshotApiKey().orEmpty()
                    val directResult = runCatching {
                        moonshot.chat(
                            request = request,
                            stock = stock,
                            metrics = fallback,
                            apiKey = key,
                            model = moonshotModel(),
                        )
                    }
                    val direct = directResult.getOrNull()
                    if (direct != null && direct.replyText.isNotBlank()) {
                        engineMode = "直连 Kimi K3"
                        direct
                    } else {
                        val reason = directResult.exceptionOrNull()?.message?.take(280) ?: "未知错误"
                        engineMode = "本地工具（直连失败）"
                        localEngine.chat(request, stockHint = stock, metricsHint = fallback).let {
                            it.copy(
                                replyText = "直连 Moonshot 失败：$reason\n\n已回退本地指标回答。\n\n${it.replyText}",
                            )
                        }
                    }
                }
                remote != null && remote.error == null && remote.replyText.isNotBlank() -> {
                    engineMode = when (remote.mode) {
                        "demo", "demo-fallback" -> "云端演示（非真实大模型）"
                        else -> "云端 ${remote.mode}"
                    }
                    remote.copy(
                        replyText = "【注意：当前为演示/模板回答，不是真实大模型】\n\n${remote.replyText}",
                    )
                }
                else -> {
                    engineMode = "本地工具（非真实大模型）"
                    localEngine.chat(request, stockHint = stock, metricsHint = fallback).let {
                        it.copy(
                            replyText = "【注意：未配置 Moonshot 密钥，以下为本地指标模板回答】\n\n${it.replyText}",
                        )
                    }
                }
            }

            var withQuant = response
            if (withQuant.scan == null && wantsScanQuestion(text) && watchlistCodes.isNotEmpty()) {
                val scan = runCatching {
                    marketRepo.scanQuant(watchlistCodes, strategyId = "consensus", lookbackDays = 90)
                }.getOrNull()
                if (scan != null) {
                    withQuant = withQuant.copy(
                        scan = scan,
                        replyText = withQuant.replyText + "\n\n自选扫描：买 ${scan.buyCount} / 卖 ${scan.sellCount} / 观望 ${scan.holdCount}",
                    )
                }
            }
            if (withQuant.quant == null && candles.size >= 20 && wantsQuantQuestion(text) && !wantsScanQuestion(text)) {
                withQuant = withQuant.copy(
                    quant = candles.localQuantPlan(
                        strategyId = detectQuantStrategyId(text),
                        lookbackDays = 90,
                        costPrice = cost,
                        quantity = qty,
                    ),
                )
            }
            sessionId = withQuant.sessionId
            plan = withQuant.structured
            val quantResult = withQuant.quant
            if (quantResult != null) {
                quant = quantResult
                selectedStrategyId = quantResult.strategyId
            }
            withQuant.scan?.let(onScanResult)
            val tools = if (withQuant.toolsCalled.isEmpty()) {
                ""
            } else {
                "\n\n已调用：${withQuant.toolsCalled.joinToString(" → ")}"
            }
            messages[thinkingIndex] = ChatMessage(
                isUser = false,
                text = withQuant.replyText + tools,
                time = "现在",
            )
            busy = false
        }
    }

    Column(
        modifier = modifier
            .fillMaxHeight()
            .background(Color(0xFFF8F9FB))
            .padding(12.dp),
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(panelScroll),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            AssistantHeader(stock, engineMode)
            HoldingCard(
                stock = stock,
                holding = holding,
                onEdit = onEditHolding,
            )

            DeskTabBar(
                selected = deskTab,
                quantSignal = quant?.signal,
                onSelect = { deskTab = it },
            )
            AppCard(
                modifier = Modifier.fillMaxWidth(),
                contentPadding = androidx.compose.foundation.layout.PaddingValues(12.dp),
            ) {
                when (deskTab) {
                    0 -> OperationPlanContent(plan, fallback)
                    else -> QuantResultContent(
                        quant = quant,
                        selectedStrategyId = selectedStrategyId,
                        lookbackDays = lookbackDays,
                        paramPresetId = paramPresetId,
                        customParams = customParams,
                        loading = quantLoading,
                        compareRows = compareRows,
                        compareLoading = compareLoading,
                        onStrategySelect = {
                            selectedStrategyId = it
                            paramPresetId = "default"
                            customParams = strategyDefaultParams(it)
                        },
                        onLookbackSelect = { lookbackDays = it },
                        onParamPresetSelect = {
                            paramPresetId = it
                            val preset = strategyParamPreset(selectedStrategyId, it)
                            customParams = strategyDefaultParams(selectedStrategyId) + preset
                        },
                        onCustomParamChange = { key, value ->
                            customParams = customParams + (key to value)
                            paramPresetId = "custom"
                        },
                        onRecordPaper = {
                            val q = quant ?: return@QuantResultContent
                            PaperTradeStore.appendSignal(
                                code = stock.code,
                                name = stock.name,
                                side = q.signal,
                                price = q.price,
                                reason = q.signalReason,
                                source = "quant",
                            )
                        },
                        onCompare = {
                            if (candles.size < 20 || compareLoading) return@QuantResultContent
                            compareLoading = true
                            scope.launch {
                                val cost = holding?.costPrice
                                val qty = holding?.quantity
                                compareRows = listOf("dual_ma", "breakout", "rsi_reversal", "consensus").map { sid ->
                                    val result = candles.localQuantPlan(
                                        strategyId = sid,
                                        lookbackDays = lookbackDays,
                                        costPrice = cost,
                                        quantity = qty,
                                    )
                                    StrategyCompareRow(
                                        strategyId = sid,
                                        name = result?.strategyName ?: sid,
                                        signal = result?.signal ?: "--",
                                        returnPct = result?.backtest?.totalReturnPct,
                                        maxDd = result?.backtest?.maxDrawdownPct,
                                        winRate = result?.backtest?.winRate,
                                    )
                                }
                                compareLoading = false
                            }
                        },
                    )
                }
            }

            QuickQuestions(onClick = { ask(it) }, enabled = !busy)

            AppCard(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    messages.forEach { message ->
                        ChatBubble(message)
                    }
                }
            }
        }

        Spacer(Modifier.height(8.dp))

        ChatInput(
            value = input,
            onValueChange = { input = it },
            enabled = !busy,
            onSend = {
                val text = input.trim()
                if (text.isNotEmpty()) {
                    input = ""
                    ask(text)
                }
            },
        )

        Text(
            if (busy) "分析中…" else "仅供研究参考，不构成投资建议",
            color = StockColors.Neutral,
            fontSize = 11.sp,
            modifier = Modifier
                .align(Alignment.CenterHorizontally)
                .padding(top = 6.dp),
        )
    }
}

@Composable
private fun DeskTabBar(
    selected: Int,
    quantSignal: String?,
    onSelect: (Int) -> Unit,
) {
    val quantLabel = when (quantSignal) {
        "BUY" -> "量化 · 买"
        "SELL" -> "量化 · 卖"
        "HOLD" -> "量化 · 观望"
        else -> "量化"
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White, RoundedCornerShape(10.dp))
            .padding(4.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        listOf("操作建议" to 0, quantLabel to 1).forEach { (label, index) ->
            val active = selected == index
            Text(
                text = label,
                color = if (active) Color.White else StockColors.TextSecondary,
                fontSize = 13.sp,
                fontWeight = if (active) FontWeight.SemiBold else FontWeight.Normal,
                modifier = Modifier
                    .weight(1f)
                    .background(
                        if (active) StockColors.PrimaryBlue else Color.Transparent,
                        RoundedCornerShape(8.dp),
                    )
                    .clickable { onSelect(index) }
                    .padding(vertical = 8.dp),
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun AssistantHeader(stock: StockItem, engineMode: String) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(54.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .size(40.dp)
                .background(Color(0xFFEAF2FF), CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text("✦", color = StockColors.PrimaryBlue, fontSize = 20.sp)
        }

        Spacer(Modifier.width(10.dp))

        Column(Modifier.weight(1f)) {
            Text(
                "智启分析师",
                color = StockColors.TextPrimary,
                fontWeight = FontWeight.Bold,
                fontSize = 19.sp,
            )
            Text(
                "当前：${stock.name} ${stock.code} · $engineMode",
                color = StockColors.TextSecondary,
                fontSize = 12.sp,
                maxLines = 1,
            )
        }
    }
}

@Composable
private fun HoldingCard(
    stock: StockItem,
    holding: HoldingPosition?,
    onEdit: () -> Unit,
) {
    val currentPrice = stock.price.replace(",", "").toDoubleOrNull()
    val pnlPct = if (holding != null && currentPrice != null && holding.costPrice > 0) {
        (currentPrice - holding.costPrice) / holding.costPrice * 100.0
    } else {
        null
    }
    val pnlColor = when {
        pnlPct == null -> StockColors.TextSecondary
        pnlPct > 0.05 -> StockColors.UpRed
        pnlPct < -0.05 -> StockColors.DownGreen
        else -> StockColors.Neutral
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White, RoundedCornerShape(10.dp))
            .clickable(onClick = onEdit)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text("持仓", color = StockColors.TextPrimary, fontSize = 14.sp, fontWeight = FontWeight.Bold)
            Text(
                if (holding != null) {
                    "成本 ${formatDecimal(holding.costPrice)} · ${holding.quantity} 股"
                } else {
                    "未录入 · 点击添加成本与数量"
                },
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
        }
        if (holding != null && pnlPct != null) {
            val sign = if (pnlPct >= 0) "+" else ""
            Text(
                "$sign${formatDecimal(pnlPct)}%",
                color = pnlColor,
                fontWeight = FontWeight.Bold,
                fontSize = 16.sp,
            )
        } else {
            Text("添加", color = StockColors.PrimaryBlue, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        }
    }
}

private data class QuantStrategyOption(val id: String, val label: String)

private data class StrategyCompareRow(
    val strategyId: String,
    val name: String,
    val signal: String,
    val returnPct: Double?,
    val maxDd: Double?,
    val winRate: Double?,
)

@Composable
private fun QuantResultContent(
    quant: QuantResultDto?,
    selectedStrategyId: String,
    lookbackDays: Int,
    paramPresetId: String,
    customParams: Map<String, String>,
    loading: Boolean,
    compareRows: List<StrategyCompareRow>,
    compareLoading: Boolean,
    onStrategySelect: (String) -> Unit,
    onLookbackSelect: (Int) -> Unit,
    onParamPresetSelect: (String) -> Unit,
    onCustomParamChange: (String, String) -> Unit,
    onRecordPaper: () -> Unit,
    onCompare: () -> Unit,
) {
    val options = listOf(
        QuantStrategyOption("dual_ma", "双均线"),
        QuantStrategyOption("breakout", "突破"),
        QuantStrategyOption("rsi_reversal", "RSI"),
        QuantStrategyOption("consensus", "共识"),
    )
    val signalColor = when (quant?.signal) {
        "BUY" -> StockColors.UpRed
        "SELL" -> StockColors.DownGreen
        else -> StockColors.TextSecondary
    }
    val signalLabel = when (quant?.signal) {
        "BUY" -> "买入"
        "SELL" -> "卖出"
        "HOLD" -> "观望"
        else -> "--"
    }
    val bt = quant?.backtest
    val presets = when (selectedStrategyId) {
        "dual_ma" -> listOf("default" to "5/20", "slow" to "10/30")
        "breakout" -> listOf("default" to "20日", "wide" to "30日")
        "rsi_reversal" -> listOf("default" to "标准", "loose" to "宽松")
        else -> emptyList()
    }
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            options.forEach { option ->
                val selected = option.id == selectedStrategyId
                Text(
                    text = option.label,
                    color = if (selected) Color.White else StockColors.TextSecondary,
                    fontSize = 11.sp,
                    fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                    modifier = Modifier
                        .background(
                            if (selected) StockColors.PrimaryBlue else Color(0xFFF0F4FA),
                            RoundedCornerShape(14.dp),
                        )
                        .clickable { onStrategySelect(option.id) }
                        .padding(horizontal = 10.dp, vertical = 5.dp),
                )
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.CenterVertically) {
            Text("回看", color = StockColors.TextSecondary, fontSize = 11.sp)
            listOf(60, 90, 120).forEach { days ->
                val selected = lookbackDays == days
                Text(
                    "${days}日",
                    color = if (selected) Color.White else StockColors.TextSecondary,
                    fontSize = 11.sp,
                    modifier = Modifier
                        .background(
                            if (selected) StockColors.PrimaryBlue else Color(0xFFF0F4FA),
                            RoundedCornerShape(12.dp),
                        )
                        .clickable { onLookbackSelect(days) }
                        .padding(horizontal = 8.dp, vertical = 4.dp),
                )
            }
        }
        if (presets.isNotEmpty()) {
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.CenterVertically) {
                Text("快捷", color = StockColors.TextSecondary, fontSize = 11.sp)
                presets.forEach { (id, label) ->
                    val selected = paramPresetId == id
                    Text(
                        label,
                        color = if (selected) Color.White else StockColors.TextSecondary,
                        fontSize = 11.sp,
                        modifier = Modifier
                            .background(
                                if (selected) StockColors.PrimaryBlue else Color(0xFFF0F4FA),
                                RoundedCornerShape(12.dp),
                            )
                            .clickable { onParamPresetSelect(id) }
                            .padding(horizontal = 8.dp, vertical = 4.dp),
                    )
                }
            }
        }
        if (customParams.isNotEmpty()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF8F9FB), RoundedCornerShape(8.dp))
                    .padding(10.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Text("策略参数", color = StockColors.TextSecondary, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                customParams.entries.sortedBy { it.key }.forEach { (key, value) ->
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(key, color = StockColors.TextSecondary, fontSize = 11.sp, modifier = Modifier.width(72.dp))
                        BasicTextField(
                            value = value,
                            onValueChange = { onCustomParamChange(key, it.filter { ch -> ch.isDigit() || ch == '.' || ch == '-' }) },
                            singleLine = true,
                            modifier = Modifier
                                .weight(1f)
                                .background(Color.White, RoundedCornerShape(6.dp))
                                .padding(horizontal = 8.dp, vertical = 6.dp),
                        )
                    }
                }
            }
        }
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                if (compareLoading) "对比中…" else "多策略对比",
                color = if (compareLoading) StockColors.Neutral else StockColors.PrimaryBlue,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier
                    .background(Color(0xFFE8F0FE), RoundedCornerShape(12.dp))
                    .clickable(enabled = !compareLoading, onClick = onCompare)
                    .padding(horizontal = 10.dp, vertical = 5.dp),
            )
            if (quant?.signal == "BUY" || quant?.signal == "SELL") {
                Text(
                    "记入流水",
                    color = StockColors.PrimaryBlue,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier
                        .background(Color(0xFFE8F0FE), RoundedCornerShape(12.dp))
                        .clickable(onClick = onRecordPaper)
                        .padding(horizontal = 10.dp, vertical = 5.dp),
                )
            }
        }
        if (compareRows.isNotEmpty()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF8F9FB), RoundedCornerShape(8.dp))
                    .padding(10.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                compareRows.forEach { row ->
                    val sigColor = when (row.signal) {
                        "BUY" -> StockColors.UpRed
                        "SELL" -> StockColors.DownGreen
                        else -> StockColors.TextSecondary
                    }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            row.name,
                            color = StockColors.TextPrimary,
                            fontSize = 11.sp,
                            modifier = Modifier
                                .weight(1f)
                                .clickable { onStrategySelect(row.strategyId) },
                        )
                        Text(
                            when (row.signal) {
                                "BUY" -> "买"
                                "SELL" -> "卖"
                                "HOLD" -> "观望"
                                else -> row.signal
                            },
                            color = sigColor,
                            fontWeight = FontWeight.Bold,
                            fontSize = 11.sp,
                            modifier = Modifier.width(36.dp),
                        )
                        Text(
                            row.returnPct?.let { "${formatDecimal(it)}%" } ?: "--",
                            color = StockColors.TextSecondary,
                            fontSize = 11.sp,
                            modifier = Modifier.width(52.dp),
                        )
                        Text(
                            row.maxDd?.let { "回撤${formatDecimal(it)}%" } ?: "",
                            color = StockColors.Neutral,
                            fontSize = 10.sp,
                        )
                    }
                }
            }
        }
        if (loading && quant == null) {
            Text("正在计算量化信号…", color = StockColors.TextSecondary, fontSize = 12.sp)
            return@Column
        }
        if (quant == null) {
            Text("K 线不足，暂无量化结果", color = StockColors.TextSecondary, fontSize = 12.sp)
            return@Column
        }
        val signalSoft = when (quant.signal) {
            "BUY" -> StockColors.UpSoft
            "SELL" -> StockColors.DownSoft
            else -> Color(0xFFF3F4F6)
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(signalSoft, RoundedCornerShape(10.dp))
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(Modifier.weight(1f)) {
                Text(
                    signalLabel + if (loading) " · 更新中" else "",
                    color = signalColor,
                    fontWeight = FontWeight.Bold,
                    fontSize = 20.sp,
                )
                Spacer(Modifier.height(2.dp))
                Text(quant.strategyName, color = StockColors.TextSecondary, fontSize = 12.sp)
            }
            quant.consensus?.let { c ->
                Column(horizontalAlignment = Alignment.End) {
                    Text("投票", color = StockColors.TextSecondary, fontSize = 10.sp)
                    Text(
                        "买${c.buyCount}/卖${c.sellCount}",
                        color = StockColors.PrimaryBlue,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 12.sp,
                    )
                }
            }
        }
        Text(quant.signalReason, color = StockColors.TextPrimary, fontSize = 12.sp, lineHeight = 18.sp)
        if (bt != null) {
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                MetricChip("胜率", "${formatDecimal(bt.winRate * 100, 0)}%", Modifier.weight(1f))
                MetricChip("收益", "${formatDecimal(bt.totalReturnPct)}%", Modifier.weight(1f))
                MetricChip("回撤", "${formatDecimal(bt.maxDrawdownPct)}%", Modifier.weight(1f))
                MetricChip("笔数", "${bt.trades}", Modifier.weight(1f))
            }
            if (bt.equityCurve.size >= 2) {
                EquityCurveMini(
                    values = bt.equityCurve,
                    up = bt.totalReturnPct >= 0,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.dp)
                        .background(Color(0xFFF8F9FB), RoundedCornerShape(8.dp))
                        .padding(horizontal = 8.dp, vertical = 6.dp),
                )
            }
            if (bt.tradeDetails.isNotEmpty()) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color(0xFFF8F9FB), RoundedCornerShape(8.dp))
                        .padding(horizontal = 10.dp, vertical = 8.dp),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Text("最近成交", color = StockColors.TextSecondary, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                    bt.tradeDetails.takeLast(5).asReversed().forEach { t ->
                        val pnlColor = if (t.returnPct >= 0) StockColors.UpRed else StockColors.DownGreen
                        Text(
                            "${t.entryDate.take(10)}→${t.exitDate.take(10)}  " +
                                "${formatDecimal(t.entryPrice)}→${formatDecimal(t.exitPrice)}  " +
                                "${if (t.returnPct >= 0) "+" else ""}${formatDecimal(t.returnPct)}%",
                            color = pnlColor,
                            fontSize = 11.sp,
                        )
                    }
                }
            }
        }
        quant.positionNote?.let {
            Text(
                it,
                color = StockColors.UpRed,
                fontSize = 12.sp,
                lineHeight = 17.sp,
                modifier = Modifier
                    .fillMaxWidth()
                    .background(StockColors.UpSoft, RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 8.dp),
            )
        }
        if (quant.entryHint != null || quant.exitHint != null) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF5F7FA), RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 8.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                quant.entryHint?.let {
                    Text("入场 $it", color = StockColors.TextSecondary, fontSize = 11.sp, lineHeight = 16.sp)
                }
                quant.exitHint?.let {
                    Text("离场 $it", color = StockColors.TextSecondary, fontSize = 11.sp, lineHeight = 16.sp)
                }
            }
        }
        Text(
            buildString {
                append("截至 ${quant.asOf}")
                val note = quant.backtest?.note.orEmpty()
                when {
                    note.contains("东财") -> append(" · 云端东财日K")
                    note.contains("演示") -> append(" · 演示K线")
                    else -> append(" · 本地引擎")
                }
            },
            color = StockColors.Neutral,
            fontSize = 11.sp,
        )
    }
}

@Composable
private fun MetricChip(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .background(Color(0xFFF5F7FA), RoundedCornerShape(8.dp))
            .padding(vertical = 6.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(value, color = StockColors.TextPrimary, fontWeight = FontWeight.Bold, fontSize = 12.sp)
        Text(label, color = StockColors.TextSecondary, fontSize = 10.sp)
    }
}

@Composable
private fun OperationPlanContent(
    plan: StructuredAnalysisDto?,
    fallback: StockTrendMetrics,
) {
    val title = plan?.conclusion?.title ?: fallback.adviceTitle
    val positionDesc = plan?.position?.description ?: fallback.positionDesc
    val buyText = plan?.buyPlan?.zones?.joinToString("\n") {
        "${formatDecimal(it.low)}～${formatDecimal(it.high)}：${it.condition}"
    } ?: fallback.buyPlan
    val riskText = plan?.let {
        "失效点 ${formatDecimal(it.risk.invalidationPrice)}；${it.risk.warnings.joinToString("；")}"
    } ?: fallback.riskHint

    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(
            title,
            color = StockColors.UpRed,
            fontWeight = FontWeight.Bold,
            fontSize = 16.sp,
        )
        Text(positionDesc, color = StockColors.TextPrimary, fontSize = 12.sp, lineHeight = 18.sp)
        Text("买入参考", color = StockColors.TextSecondary, fontWeight = FontWeight.SemiBold, fontSize = 11.sp)
        Text(buyText, color = StockColors.TextPrimary, fontSize = 12.sp, lineHeight = 18.sp)
        Text(riskText, color = StockColors.TextSecondary, fontSize = 11.sp, lineHeight = 16.sp)
    }
}

@Composable
private fun QuickQuestions(
    onClick: (String) -> Unit,
    enabled: Boolean,
) {
    val questions = listOf(
        "现在能买吗？",
        "双均线回测",
        "多策略共识",
        "扫描自选",
        "整体分析",
    )
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        questions.forEach { question ->
            QuestionChip(question, enabled, Modifier.weight(1f)) { onClick(it) }
        }
    }
}

@Composable
private fun QuestionChip(
    text: String,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    onClick: (String) -> Unit,
) {
    Text(
        text = text,
        color = if (enabled) StockColors.TextSecondary else StockColors.Neutral,
        fontSize = 11.sp,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = TextAlign.Center,
        modifier = modifier
            .background(Color(0xFFF0F4FA), RoundedCornerShape(16.dp))
            .clickable(enabled = enabled) { onClick(text) }
            .padding(horizontal = 4.dp, vertical = 7.dp),
    )
}

@Composable
private fun ChatBubble(message: ChatMessage) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = if (message.isUser) Arrangement.End else Arrangement.Start,
        verticalAlignment = Alignment.Bottom,
    ) {
        if (!message.isUser) {
            Box(
                Modifier
                    .size(28.dp)
                    .background(Color(0xFFEAF2FF), CircleShape),
                contentAlignment = Alignment.Center,
            ) {
                Text("✦", color = StockColors.PrimaryBlue, fontSize = 14.sp)
            }
            Spacer(Modifier.width(7.dp))
        }

        Column(
            horizontalAlignment = if (message.isUser) Alignment.End else Alignment.Start,
        ) {
            Box(
                modifier = Modifier
                    .background(
                        color = if (message.isUser) StockColors.PrimaryBlue else Color.White,
                        shape = RoundedCornerShape(
                            topStart = 12.dp,
                            topEnd = 12.dp,
                            bottomStart = if (message.isUser) 12.dp else 3.dp,
                            bottomEnd = if (message.isUser) 3.dp else 12.dp,
                        ),
                    )
                    .padding(horizontal = 12.dp, vertical = 9.dp),
            ) {
                val base = if (message.isUser) Color.White else StockColors.TextPrimary
                Text(
                    text = if (message.isUser) {
                        AnnotatedString(message.text)
                    } else {
                        highlightChatText(message.text, base)
                    },
                    color = base,
                    fontSize = 12.sp,
                    lineHeight = 18.sp,
                )
            }
            Spacer(Modifier.height(3.dp))
            Text(message.time, color = StockColors.Neutral, fontSize = 9.sp)
        }
    }
}

@Composable
private fun ChatInput(
    value: String,
    onValueChange: (String) -> Unit,
    onSend: () -> Unit,
    enabled: Boolean = true,
) {
    AppCard(
        modifier = Modifier
            .fillMaxWidth()
            .height(58.dp),
    ) {
        Row(
            modifier = Modifier
                .fillMaxHeight()
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                enabled = enabled,
                singleLine = true,
                textStyle = androidx.compose.ui.text.TextStyle(
                    color = StockColors.TextPrimary,
                    fontSize = 13.sp,
                    fontFamily = LocalAppFontFamily.current,
                ),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Send),
                keyboardActions = KeyboardActions(onSend = { if (enabled) onSend() }),
                modifier = Modifier.weight(1f),
                decorationBox = { inner ->
                    Box {
                        if (value.isBlank()) {
                            Text(
                                if (enabled) "输入问题，例如：现在能买吗？" else "分析中，请稍候…",
                                color = StockColors.Neutral,
                                fontSize = 13.sp,
                            )
                        }
                        inner()
                    }
                },
            )

            Box(
                modifier = Modifier
                    .size(36.dp)
                    .background(
                        if (enabled && value.isNotBlank()) StockColors.PrimaryBlue else Color(0xFFCBD5E1),
                        CircleShape,
                    )
                    .clickable(enabled = enabled && value.isNotBlank(), onClick = onSend),
                contentAlignment = Alignment.Center,
            ) {
                Text("➤", color = Color.White, fontSize = 16.sp)
            }
        }
    }
}
